// Copyright (c) 2022 by Move Closer

import { AnyObject } from '@movecloser/front-core'

import { CartModel } from '../../../front/checkout/contracts'
import { ShipmentRegion } from '../../../front/shared/services/dictionary'

import { ProductSneakPeakData } from '../../products'

export interface CartAddress {
  firstname: string
  lastname: string
  company?: string
  street: string[]
  city: string
  region?: string | ShipmentRegion
  postcode?: string
  countryCode: string
  telephone?: string
  vatId?: string
  saveInAddressBook?: boolean
}

export interface CartBillingAddress extends CartAddress {
  sameAsShipping?: boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartCoupon {
  code: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartData<P extends AnyObject = ProductSneakPeakData> {
  // availablePaymentMethods: PaymentMethodPayload[]
  // availableShippingMethods: ShippingMethod[]
  billingAddress: CartBillingAddress | null
  coupons: CartCoupon[]
  discounts: Array<CartDiscount>
  email?: string
  id: CartIdentifier
  items: CartItemData<P>[]
  note: string | null
  payment: PaymentMethodCode | null
  selectedPaymentMethod: PaymentMethodPayload | null
  selectedPaymentMethodId: number
  selectedShippingMethod: ShippingMethod | null
  shippingAddress: CartShippingAddress | null
  subtotal: CartMoneyDescription
  subtotalExclTaxes?: CartMoneyDescription
  taxes: Array<CartTax>
  total: CartMoneyDescription
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartDiscount {
  amount: CartMoneyDescription
  label: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type CartIdentifier = string

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartItemData<P extends AnyObject = ProductSneakPeakData> {
  appliedCoupons: Array<string>
  isGift: boolean
  isSample: boolean
  product: P
  prices: CartItemPrice
  quantity: number
  outOfStock?: boolean
  uid: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartItemPrice {
  price: CartMoneyDescription
  total: CartMoneyDescription
  totalDiscount: CartMoneyDescription
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartMoneyDescription {
  value: number
  currency: string
}

export interface CartShippingAddress extends CartAddress {
  customerNotes?: string
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CartTax {
  amount: CartMoneyDescription
  label: string
}

export interface DeliveryTime {
  deliveryFrom: string
  deliveryTo: string
}

// these contracts come from magento, that's why attributes are snake-case
export interface MagentoCartCountry {
  id: string
  // eslint-disable-next-line
  full_name_locale: string
}

export interface MagentoShippingMethod {
  amount: {
    currency: string
    value: number
  }
  // eslint-disable-next-line
  carrier_code: ShippingCarrierCode
  // eslint-disable-next-line
  delivery_time?: DeliveryTime
  // eslint-disable-next-line
  method_code: ShippingMethodCode
  label: string
  // eslint-disable-next-line
  price_incl_tax: {
    currency: string
    value: number
  }
}

export interface OrderResponse {
  orderNumber: string
  instructions: string
  paymentMethod: PaymentMethodCode
  payuRedirectUri: string | null
  paynowRedirectUri: string | null
  przelewy24PaymentUrl: string | null
  stripePaymentUrl: string | null
  proformaInvoiceLink: string | null
  ngeniusPaymentUrl: string | null
}

export interface OrderData extends OrderResponse {
  total: string
  cart: CartModel
  deliveryFrom?: string
  deliveryTo?: string
}

export interface PaymentMethodPayload<C extends PaymentMethodCode | string = PaymentMethodCode> {
  blik?: number
  methodId?: number
  code: C
}

export enum PaymentMethodCode {
  ApplePay = 'apple-pay', // todo
  Blik = 'paynow_blik_gateway',
  GooglePay = 'gpay', // todo
  OnDelivery = 'cashondelivery',
  PayPal = 'paypal', // todo
  PayU = 'payu_gateway',
  PayUCard = 'payu_gateway_card',
  PayNow = 'paynow_gateway',
  Proforma = 'proforma',
  Przelewy24 = 'przelewy24',
  Przelewy24Blik = 'przelewy24_blik',
  Przelewy24InternalBlik = '154',
  Przelewy24Card = '147',
  Przelewy24InternalBanktransfer = '178',
  Ngenius = 'ngeniusonline',
  Przelewy24InternalApple = '239',
  Przelewy24InternalGoogle = '238',
  Przelewy24PayPo = '227',
  Stripe = 'stripe_payments_checkout',
  Transfer = 'banktransfer'
}

export interface ShippingDetails {
  deliveryTime: DeliveryTime
  disabledPaymentMethods: string[]
  displayInfo?: string
}

/**
 * Dictionary of all available shipping methods.
 */
export enum ShippingMethodCode {
  DHLKurier = 'Dhlshipping',
  DPDKurier = 'Dpdshipping',
  Flatrate = 'flatrate',
  // DPDKurier,
  // InpostKurier,
  Free = 'freeshipping',
  OrlenPaczka = '95_0',
  OrlenPaczkaPobranie = '95_1',
  // Paczkomat,
  Paczkomat = 'inpost', // FIXME: Set correct code.
  PaczkomatCod = 'inpostcod',
  UPS = 'upsshipping',
  DPD = 'Dpdshipping',
  DPDCod = 'Dpdshippingcod',
  //
  // Aramex Providers
  //
  AramexSameDayDocument = 'SMD',
  AramexOverNightDocument = 'OND',
  AramexOverNightParcel = 'ONP',
  AramexCreditsCardsDelivery = 'CDS'
}

export type ShippingCarrierCode = ShippingMethodCode

export interface ShippingMethod {
  carrierCode: ShippingCarrierCode
  deliveryTime?: DeliveryTime
  displayInfo?: string
  methodCode: ShippingMethodCode
  price: CartMoneyDescription
}

export interface AvailableShippingMethod {
  carrierCode: ShippingCarrierCode
  methodCode: ShippingMethodCode
  price: CartMoneyDescription
}

export interface AvailablePaymentMethod<C extends PaymentMethodCode | string = PaymentMethodCode> extends PaymentMethodPayload<C> {
  title: string
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface UnresolvedCartItemProduct {
  sku: string
}
